import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";
import { site } from "@/mocks/site";

function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: "zh-CN",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    }),
    []
  );

  useSeo({
    title: "常见问题 - 匿名投稿、隐私与核实流程说明 | 爆料平台",
    description:
      "爆料平台常见问题解答：投稿是否需要实名、可以提交哪些材料、多久有反馈、平台如何处理不同说法、相关方如何申请更正，以及线索公开后能否修改。",
    keywords: "爆料常见问题,匿名投稿,隐私保护,核实流程,更正申请",
    path: "/faq",
    jsonLd,
  });

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            常见问题
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于匿名、材料类型、反馈时间与更正流程，这里集中回答最常被问到的几个问题。
          </p>
        </div>
      </div>

      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="divide-y divide-background-200 border-y border-background-200">
              {faqs.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                  <section key={item.q} itemScope itemType="https://schema.org/Question">
                    <h2 itemProp="name" className="m-0">
                      <button
                        type="button"
                        onClick={() => setOpenIndex(isOpen ? null : index)}
                        aria-expanded={isOpen}
                        className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors hover:text-primary-700"
                      >
                        <span className="font-heading text-base font-semibold text-foreground-950 md:text-lg">
                          {item.q}
                        </span>
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-600">
                          <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} />
                        </span>
                      </button>
                    </h2>
                    {isOpen && (
                      <div
                        itemScope
                        itemType="https://schema.org/Answer"
                        className="animate-fade-in pb-6 pr-10"
                      >
                        <p itemProp="text" className="text-sm leading-relaxed text-foreground-600">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">还没解决你的疑问？</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                可以通过联系入口提交问题，或直接发送邮件给我们。{site.reviewCycle}
              </p>
              <div className="mt-4 space-y-3 text-sm text-foreground-700">
                <p className="flex items-start gap-2">
                  <i className="ri-mail-line mt-0.5 text-primary-600" />
                  <span className="break-all">{site.email}</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-inbox-archive-line mt-0.5 text-primary-600" />
                  <span className="break-all">投稿专用：{site.tipEmail}</span>
                </p>
              </div>
              <Link
                to="/contact"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-customer-service-2-line" />
                联系平台
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default FaqPage;